/* eslint-disable max-lines */
import { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  Typography,
  Skeleton,
} from '@mui/material'
import { useTheme, alpha } from '@mui/material/styles'
import { getYear } from 'date-fns'
import { useI18n, useRouter } from '@popety_io/popety-io-lib'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import { EventHistory } from '../EventHistory'
import EstimateTransactionDetail from '../EstimateTransactionDetail'
import FaoDetail from '../FaoDetail'
import ListingDetail from '../ListingDetail'
import { estimateSelector } from '../../redux'

enum EVENT_HISTORY_CATEGORY {
  FAO = 'Fao',
  TRANSACTION = 'Transaction',
  RENTAL = 'Rental',
  SALE = 'Sale',
}

const EstimateEventHistory = () => {
  const { t } = useI18n()
  const [event, setEvent] = useState<any>({})

  const { updateQuery } = useRouter()

  const estimate = useSelector(estimateSelector)

  const faosAreActive =
    estimate?.historySections?.[EVENT_HISTORY_CATEGORY.FAO] !== false

  const transactionsAreActive =
    estimate?.region_name === 'Genève' &&
    estimate?.historySections?.[EVENT_HISTORY_CATEGORY.TRANSACTION] !== false

  const rentalsAreActive =
    estimate?.historySections?.[EVENT_HISTORY_CATEGORY.RENTAL] !== false

  const salesAreActive =
    estimate?.historySections?.[EVENT_HISTORY_CATEGORY.SALE] !== false

  const transactions = useMemo(() => {
    return transactionsAreActive ? estimate?.historyTransactions : []
  }, [estimate])

  const faos = useMemo(() => {
    return faosAreActive ? estimate?.historyFaos || [] : []
  }, [estimate])

  const listings = useMemo(() => {
    return estimate?.historyListings || []
  }, [estimate])

  const rentListings = useMemo(() => {
    return rentalsAreActive
      ? listings?.filter((listing: any) => listing?.deal_type === 'rent')
      : []
  }, [listings])

  const purchaseListings = useMemo(() => {
    return salesAreActive
      ? listings?.filter((listing: any) => listing?.deal_type === 'purchase')
      : []
  }, [listings])

  const transactionList = [...(transactions || [])].sort((a, b) =>
    a.date > b.date ? -1 : 1,
  )

  const countEvent = 10

  const theme = useTheme()

  const [checked, setChecked] = useState<any[]>([
    { key: EVENT_HISTORY_CATEGORY.FAO, checked: true },
    { key: EVENT_HISTORY_CATEGORY.TRANSACTION, checked: true },
    { key: EVENT_HISTORY_CATEGORY.RENTAL, checked: true },
    { key: EVENT_HISTORY_CATEGORY.SALE, checked: true },
  ])

  const checkMarks = [
    faosAreActive && {
      label: t('common.Mise à l’enquête'),
      count: faos?.length ?? 0,
      key: 'Fao',
      color: theme.palette.primary.main,
    },
    transactionsAreActive &&
      estimate?.region_name === 'Genève' && {
        label: t('common.Transaction'),
        count: transactionList?.length ?? 0,
        key: 'Transaction',
        color: alpha(theme.palette.primary.main, 0.7),
      },
    rentalsAreActive !== false && {
      label: t('common.Annonce de location'),
      count: rentListings?.length ?? 0,
      key: 'Rental',
      color: alpha(theme.palette.primary.main, 0.3),
    },
    salesAreActive && {
      label: t('common.Annonce de vente'),
      count: purchaseListings?.length ?? 0,
      key: 'Sale',
      color: alpha(theme.palette.primary.main, 0.3),
    },
  ].filter(Boolean)

  const handleChange = (key: string) => {
    setChecked((prev) => {
      return prev?.map((item) => {
        if (item.key === key) {
          return {
            ...item,
            checked: !item.checked,
          }
        }

        return item
      })
    })
  }

  const handleSelectEvent = (event: any) => {
    setEvent(event)
  }

  const listEventHistoryData = [
    ...transactionList.map((item: any) => {
      return {
        type: EVENT_HISTORY_CATEGORY.TRANSACTION,
        ...item,
      }
    }),
    ...faos.map((item: any) => {
      return {
        type: EVENT_HISTORY_CATEGORY.FAO,
        date: item?.inquiry_start_date,
        ...item,
      }
    }),
    ...rentListings.map((item: any) => {
      return {
        type: EVENT_HISTORY_CATEGORY.RENTAL,
        date: item?.listing_timestamp,
        ...item,
      }
    }),
    ...purchaseListings.map((item: any) => {
      return {
        type: EVENT_HISTORY_CATEGORY.SALE,
        date: item?.listing_timestamp,
        ...item,
      }
    }),
  ]

  const listEventHistory = useMemo(() => {
    const data = listEventHistoryData?.filter((item: any) => {
      return checked?.find((check) => check?.key === item?.type)?.checked
    })

    const sortedListEventHistoryData = data.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })

    return sortedListEventHistoryData
  }, [checked, listEventHistoryData])

  const loading = useMemo(() => {
    return false
  }, [])

  useEffect(() => {
    if (event?.date) {
      const year = new Date(event.date).getFullYear()

      updateQuery({
        historyLayer: year === getYear(new Date()) ? year - 1 : year,
      })
    } else {
      updateQuery({ historyLayer: getYear(new Date()) - 1 })
    }
  }, [event])

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Box
          position="sticky"
          top={0}
          zIndex={10}
          bgcolor="background.paper"
          sx={{
            pb: 1,
            pt: 2,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              marginBottom: '4px',
            }}
            fontSize="1.7rem"
            fontWeight={500}
          >
            {t('common.historique')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#969696',
              paddingBottom: '10px',
            }}
            fontSize="1rem"
          >
            {t('common.history.history_description')}
          </Typography>
          {countEvent > 0 && !event?.id ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                top: 0,
                zIndex: 1000,
                background: '#fff',
                width: '100%',
                marginLeft: '-9px',
                justifyContent: 'space-between',
              }}
            >
              {checkMarks?.map((mark: any, indexMark: number) => (
                <MenuItem
                  key={indexMark}
                  value={mark.label}
                  sx={{
                    paddingX: 0,
                  }}
                  onClick={() => handleChange(mark.key)}
                >
                  <Checkbox
                    checked={
                      checked.find((item: any) => item.key === mark.key)
                        ?.checked
                    }
                    name={mark.key}
                    disabled={loading}
                    sx={{
                      '&.Mui-checked': {
                        color: mark.color,
                      },
                    }}
                  />
                  <ListItemText
                    primary={`${mark.label} (${mark.count})`}
                    sx={{
                      paddingRight: '10px',
                    }}
                  />
                </MenuItem>
              ))}
            </Box>
          ) : (
            ''
          )}
        </Box>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {Array.from({ length: 6 })?.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: '100%',
                  mt: 1.5,
                  display: 'flex',
                }}
              >
                <Box
                  sx={{
                    width: '16%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    sx={{
                      fontSize: '1rem',
                      borderRadius: 2,
                      height: '15px',
                      width: '80px',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <Skeleton
                      variant="rounded"
                      sx={{
                        fontSize: '0.8rem',
                        borderRadius: 2,
                        height: '15px',
                        width: '4px',
                      }}
                    />
                    <Skeleton variant="circular" width={30} height={30} />
                    <Skeleton
                      variant="rounded"
                      sx={{
                        fontSize: '0.8rem',
                        borderRadius: 2,
                        height: '15px',
                        width: '4px',
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: '84.5%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: 2,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    sx={{
                      fontSize: '1rem',
                      borderRadius: 2,
                      height: '50px',
                      width: '100%',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {event && event?.type === EVENT_HISTORY_CATEGORY.TRANSACTION && (
          <Box
            pt={0}
            zIndex={10}
            bgcolor="background.paper"
            sx={{
              position: {
                lg: 'sticky',
              },
              top: {
                lg: 0,
              },
            }}
          >
            <EstimateTransactionDetail
              transaction={event}
              setTransaction={setEvent}
            />
          </Box>
        )}
        {event && event?.type === EVENT_HISTORY_CATEGORY.FAO && (
          <Box
            pt={0}
            zIndex={10}
            bgcolor="background.paper"
            sx={{
              position: {
                lg: 'sticky',
              },
              top: {
                lg: 0,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                marginTop: 1,
              }}
              onClick={() => {
                setEvent({})
              }}
            >
              <ArrowBackIosIcon
                sx={{
                  width: '0.8em',
                  height: '0.8em',
                }}
              />
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '1.1rem',
                }}
              >
                {t('estimate.comparables.retour à la liste')}
              </Typography>
            </Box>
            <FaoDetail fao={event} setFao={setEvent} />
          </Box>
        )}
        {event &&
          (event?.type === EVENT_HISTORY_CATEGORY.RENTAL ||
            event?.type === EVENT_HISTORY_CATEGORY.SALE) && (
            <Box
              pt={0}
              zIndex={10}
              bgcolor="background.paper"
              sx={{
                position: {
                  lg: 'sticky',
                },
                top: {
                  lg: 0,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  marginTop: 1,
                }}
                onClick={() => {
                  setEvent({})
                }}
              >
                <ArrowBackIosIcon
                  sx={{
                    width: '0.8em',
                    height: '0.8em',
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '1.1rem',
                  }}
                >
                  {t('estimate.comparables.retour à la liste')}
                </Typography>
              </Box>
              <ListingDetail listing={event} setListing={setEvent} />
            </Box>
          )}
        {!loading && (!event || Object.keys(event)?.length === 0) && (
          <EventHistory
            listEventHistory={listEventHistory}
            checked={checked}
            handleClickSeeMore={() => {}}
            event={event}
            handleSelectEvent={handleSelectEvent}
            sx={{
              display: !(event?.id && event?._id) ? '' : 'none',
              marginLeft: '-9px',
            }}
          />
        )}
        {!loading && listEventHistory.length === 0 && (
          <Typography
            sx={{
              textAlign: 'center',
              marginTop: '20px',
            }}
          >
            {t('common.no_data_available')}
          </Typography>
        )}
      </Box>
    </div>
  )
}

export default EstimateEventHistory
